import { G } from '@mobily/ts-belt';
import { Decimal } from 'decimal.js';
import { match } from 'ts-pattern';
import { z } from 'zod';

import {
  EMPLOYEE_SAVINGS_MAX_MINIMUM_TENURE_MONTHS,
  EMPLOYEE_SAVINGS_MAX_UNILATERAL_MATCHING_CONTRIBUTION_CENTS,
  EMPLOYEE_SAVINGS_PASS_PERCENTAGE,
} from '@Common/constants/employee_savings';
import {
  B2bSavingPlanCategory,
  B2bSavingPlanType,
  b2bCompanyPlanTypeToCategory,
  isPerecoiPilotePlan,
  ZodB2bSavingPlanCategory,
  ZodB2bSavingPlanType,
} from '@Common/types/b2b/plans';
import { ZodB2bCashSource } from '@Common/types/bo/b2b/b2b';
import {
  type B2bContractType,
  ZodB2bContractType,
} from '@Common/types/bo/b2b/contracts/contract_type';
import {
  type ByEnvironmentInsurer,
  getContractTypeDefinition,
  type PlanDefinition,
} from '@Common/types/bo/b2b/contracts/contract_type_definitions';
import { EmployeeStatusValues, ZodEmployeeStatus } from '@Common/types/bo/b2b/employees_properties';
import { ZodMatchingContributionConfig } from '@Common/types/bo/b2b/matching_contribution_config';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodEmployeeSavingsRiskProfile } from '@Common/types/profile/employee_savings';
import { ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const B2bCcbOptionAtMaturityValues = [
  'consultation',
  'final_withdrawal',
  'final_arbitrage',
] as const;
export type B2bCcbOptionAtMaturity = (typeof B2bCcbOptionAtMaturityValues)[number];

export const B2bCcbPayerOfLiquidityValues = ['company', 'account_keeper'] as const;
export type B2bCcbPayerOfLiquidity = (typeof B2bCcbPayerOfLiquidityValues)[number];

export const ZodB2bCcbConfig = z.object({
  option_at_maturity: z.enum(B2bCcbOptionAtMaturityValues),
  payer_of_liquidity: z.enum(B2bCcbPayerOfLiquidityValues),
  handover_date: ZodTrimmedString,
  is_arbitrage_allowed_into_per: z.boolean().optional().default(false),
});
export type B2bCcbConfig = z.infer<typeof ZodB2bCcbConfig>;

export const B2bPlanFeePaidByValues = ['company', 'employee'] as const;
export type B2bPlanFeePaidBy = (typeof B2bPlanFeePaidByValues)[number];

export const B2bPlanEntryFeePaidByValues = ['company', 'user'] as const;
export type B2bPlanEntryFeePaidBy = (typeof B2bPlanEntryFeePaidByValues)[number];

export const B2bMatchingContributionComputedByValues = ['company', 'distributor'] as const;
export type B2bMatchingContributionComputedBy =
  (typeof B2bMatchingContributionComputedByValues)[number];

export const b2bCompanyPlanInputTypeToValues = (
  type: B2bSavingPlanCategory | B2bSavingPlanType,
): B2bSavingPlanType[] => {
  return match(type)
    .with('PEI', () => ['PEI_LIBRE' as const, 'PEI_PROFILE' as const])
    .with('PEI_LIBRE', () => ['PEI_LIBRE' as const])
    .with('PEI_PROFILE', () => ['PEI_PROFILE' as const])
    .with('PEE', () => ['PEE_LIBRE' as const, 'PEE_PROFILE' as const])
    .with('PEE_LIBRE', () => ['PEE_LIBRE' as const])
    .with('PEE_PROFILE', () => ['PEE_PROFILE' as const])
    .with('PEEG', () => ['PEEG_LIBRE' as const, 'PEEG_PROFILE' as const])
    .with('PEEG_LIBRE', () => ['PEEG_LIBRE' as const])
    .with('PEEG_PROFILE', () => ['PEEG_PROFILE' as const])
    .with('PERECOI', () => ['PERECOI_LIBRE' as const, 'PERECOI_PILOTE' as const])
    .with('PERECOI_LIBRE', () => ['PERECOI_LIBRE' as const])
    .with('PERECOI_PILOTE', () => ['PERECOI_PILOTE' as const])
    .with('PERECO', () => ['PERECO_LIBRE' as const, 'PERECO_PILOTE' as const])
    .with('PERECO_LIBRE', () => ['PERECO_LIBRE' as const])
    .with('PERECO_PILOTE', () => ['PERECO_PILOTE' as const])
    .with('PERECOG', () => ['PERECOG_LIBRE' as const, 'PERECOG_PILOTE' as const])
    .with('PERECOG_LIBRE', () => ['PERECOG_LIBRE' as const])
    .with('PERECOG_PILOTE', () => ['PERECOG_PILOTE' as const])
    .with('PERCOI', () => ['PERCOI_LIBRE' as const, 'PERCOI_PILOTE' as const])
    .with('PERCOI_LIBRE', () => ['PERCOI_LIBRE' as const])
    .with('PERCOI_PILOTE', () => ['PERCOI_PILOTE' as const])
    .with('PERCO', () => ['PERCO_LIBRE' as const, 'PERCO_PILOTE' as const])
    .with('PERCO_LIBRE', () => ['PERCO_LIBRE' as const])
    .with('PERCO_PILOTE', () => ['PERCO_PILOTE' as const])
    .with('PERO', () => ['PERO_LIBRE' as const, 'PERO_PILOTE' as const])
    .with('PERO_LIBRE', () => ['PERO_LIBRE' as const])
    .with('PERO_PILOTE', () => ['PERO_PILOTE' as const])
    .with('PERU', () => ['PERU_LIBRE' as const, 'PERU_PILOTE' as const])
    .with('PERU_LIBRE', () => ['PERU_LIBRE' as const])
    .with('PERU_PILOTE', () => ['PERU_PILOTE' as const])
    .with('PER', () => ['PER_LIBRE' as const, 'PER_PILOTE' as const])
    .with('PER_LIBRE', () => ['PER_LIBRE' as const])
    .with('PER_PILOTE', () => ['PER_PILOTE' as const])
    .with('ARTICLE_83', () => ['ARTICLE_83_LIBRE' as const, 'ARTICLE_83_PILOTE' as const])
    .with('ARTICLE_83_LIBRE', () => ['ARTICLE_83_LIBRE' as const])
    .with('ARTICLE_83_PILOTE', () => ['ARTICLE_83_PILOTE' as const])
    .with('ARTICLE_82', () => ['ARTICLE_82_LIBRE' as const, 'ARTICLE_82_PILOTE' as const])
    .with('ARTICLE_82_LIBRE', () => ['ARTICLE_82_LIBRE' as const])
    .with('ARTICLE_82_PILOTE', () => ['ARTICLE_82_PILOTE' as const])
    .with('PERECOER', () => ['PERECOER_LIBRE' as const, 'PERECOER_PILOTE' as const])
    .with('PERECOER_LIBRE', () => ['PERECOER_LIBRE' as const])
    .with('PERECOER_PILOTE', () => ['PERECOER_PILOTE' as const])
    .with('ER_EXTERNAL', () => ['ER_EXTERNAL' as const])
    .exhaustive();
};

export const B2bCompanyPlanTypeParameters: Record<
  B2bSavingPlanType | B2bSavingPlanCategory,
  { name: string; name_short: string; social_contribution: number }
> = {
  PEI: {
    name: 'PEI Soles - (Profilé & Libre)',
    name_short: 'PEI Soles',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEI,
  },
  PEI_LIBRE: {
    name: 'PEI Soles - Libre',
    name_short: 'PEI Soles - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEI,
  },
  PEI_PROFILE: {
    name: 'PEI Soles - Profilé',
    name_short: 'PEI Soles - Profilé',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEI,
  },
  PEE: {
    name: 'PEE - (Profilé & Libre)',
    name_short: 'PEE',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEE,
  },
  PEE_LIBRE: {
    name: 'PEE - Libre',
    name_short: 'PEE - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEE,
  },
  PEE_PROFILE: {
    name: 'PEE - Profilé',
    name_short: 'PEE - Profilé',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEE,
  },
  PEEG: {
    name: 'PEE Groupe - (Profilé & Libre)',
    name_short: 'PEE Groupe',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEEG,
  },
  PEEG_LIBRE: {
    name: 'PEE Groupe - Libre',
    name_short: 'PEE Groupe - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEEG,
  },
  PEEG_PROFILE: {
    name: 'PEE Groupe - Profilé',
    name_short: 'PEE Groupe - Profilé',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PEEG,
  },
  PERECOI_PILOTE: {
    name: 'PERECO-I Soles - Piloté',
    name_short: 'PERECO-I Soles',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOI,
  },
  PERECOI_LIBRE: {
    name: 'PERECO-I Soles - Libre',
    name_short: 'PERECO-I Soles - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOI,
  },
  PERECOI: {
    name: 'PERECO-I Soles - (Piloté & Libre)',
    name_short: 'PERECO-I Soles',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOI,
  },
  PERECO_PILOTE: {
    name: 'PERECO - Piloté',
    name_short: 'PERECO - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECO,
  },
  PERECO_LIBRE: {
    name: 'PERECO - Libre',
    name_short: 'PERECO - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECO,
  },
  PERECO: {
    name: 'PERECO - (Piloté & Libre)',
    name_short: 'PERECO',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECO,
  },
  PERECOG_PILOTE: {
    name: 'PERECO Groupe - Piloté',
    name_short: 'PERECO Groupe - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOG,
  },
  PERECOG_LIBRE: {
    name: 'PERECO Groupe - Libre',
    name_short: 'PERECO Groupe - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOG,
  },
  PERECOG: {
    name: 'PERECO Groupe - (Piloté & Libre)',
    name_short: 'PERECO Groupe',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOG,
  },
  PERCOI: {
    name: 'PERCO-I Soles - (Piloté & Libre)',
    name_short: 'PERCO-I Soles',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERCOI,
  },
  PERCOI_PILOTE: {
    name: 'PERCO-I Soles - Piloté',
    name_short: 'PERCO-I Soles - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERCOI,
  },
  PERCOI_LIBRE: {
    name: 'PERCO-I Soles - Libre',
    name_short: 'PERCO-I Soles - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERCOI,
  },
  PERCO: {
    name: 'PERCO Soles - (Piloté & Libre)',
    name_short: 'PERCO Soles',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERCO,
  },
  PERCO_PILOTE: {
    name: 'PERCO Soles - Piloté',
    name_short: 'PERCO Soles - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERCO,
  },
  PERCO_LIBRE: {
    name: 'PERCO Soles - Libre',
    name_short: 'PERCO Soles - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERCO,
  },
  PERO: {
    name: 'PERO - (Piloté & Libre)',
    name_short: 'PERO',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERO,
  },
  PERO_PILOTE: {
    name: 'PERO - Piloté',
    name_short: 'PERO - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERO,
  },
  PERO_LIBRE: {
    name: 'PERO - Libre',
    name_short: 'PERO - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERO,
  },
  PERU: {
    name: 'PERU - (Piloté & Libre)',
    name_short: 'PERU',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERU,
  },
  PERU_PILOTE: {
    name: 'PERU - Piloté',
    name_short: 'PERU - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERU,
  },
  PERU_LIBRE: {
    name: 'PERU - Libre',
    name_short: 'PERU - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERU,
  },
  PER: {
    name: 'SOLERE PER - (Piloté & Libre)',
    name_short: 'SOLERE PER',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PER,
  },
  PER_PILOTE: {
    name: 'SOLERE PER - Piloté',
    name_short: 'SOLERE PER - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PER,
  },
  PER_LIBRE: {
    name: 'SOLERE PER - Libre',
    name_short: 'SOLERE PER - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PER,
  },
  ARTICLE_83: {
    name: 'ARTICLE 83 - (Piloté & Libre)',
    name_short: 'ARTICLE 83',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ARTICLE_83,
  },
  ARTICLE_83_LIBRE: {
    name: 'ARTICLE 83 - Libre',
    name_short: 'ARTICLE 83 - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ARTICLE_83,
  },
  ARTICLE_83_PILOTE: {
    name: 'ARTICLE 83 - Piloté',
    name_short: 'ARTICLE 83 - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ARTICLE_83,
  },
  ARTICLE_82: {
    name: 'ARTICLE 82 - (Piloté & Libre)',
    name_short: 'ARTICLE 82',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ARTICLE_82,
  },
  ARTICLE_82_LIBRE: {
    name: 'ARTICLE 82 - Libre',
    name_short: 'ARTICLE 82 - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ARTICLE_82,
  },
  ARTICLE_82_PILOTE: {
    name: 'ARTICLE 82 - Piloté',
    name_short: 'ARTICLE 82 - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ARTICLE_82,
  },
  PERECOER: {
    name: 'PERECO ER - (Piloté & Libre)',
    name_short: 'PERECO ER',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOER,
  },
  PERECOER_LIBRE: {
    name: 'PERECO ER - Libre',
    name_short: 'PERECO ER - Libre',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOER,
  },
  PERECOER_PILOTE: {
    name: 'PERECO ER - Piloté',
    name_short: 'PERECO ER - Piloté',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.PERECOER,
  },
  ER_EXTERNAL: {
    name: 'ER EXTERNAL',
    name_short: 'EXTERNAL ER',
    social_contribution: EMPLOYEE_SAVINGS_PASS_PERCENTAGE.ER_EXTERNAL,
  },
};

/** Rules of a plan category on contracts of the given type, when it has any */
export function getPlanDefinition(
  contractType: B2bContractType | null | undefined,
  category: B2bSavingPlanCategory | null | undefined,
): PlanDefinition | undefined {
  return G.isNotNullable(contractType) && G.isNotNullable(category)
    ? getContractTypeDefinition(contractType).plans[category]
    : undefined;
}

function forEnvironment<T extends string | readonly string[]>(
  value: ByEnvironmentInsurer<T>,
  environment: { mlk: boolean },
): T {
  return typeof value === 'object' && 'axa' in value
    ? value[environment.mlk ? 'malakoff' : 'axa']
    : value;
}

/**
 * ISIN of the fund plans of a category invest in when employees make no choice and the plan does
 * not pick its own, if any
 */
export function getDefaultFundIsin(
  contractType: B2bContractType,
  category: B2bSavingPlanCategory,
  environment: { mlk: boolean },
): string | undefined {
  const { defaultFundIsin } = getContractTypeDefinition(contractType);
  return (
    getPlanDefinition(contractType, category)?.defaultFundIsin?.default ??
    (defaultFundIsin === undefined ? undefined : forEnvironment(defaultFundIsin, environment))
  );
}

/** ISINs of the funds the BO prefills the instruments universe of new plans of a category with */
export function getInstrumentsUniversePrefillIsins(
  contractType: B2bContractType,
  category: B2bSavingPlanCategory,
  environment: { mlk: boolean },
): readonly string[] {
  const { instrumentsUniversePrefillIsins } = getContractTypeDefinition(contractType);
  return (
    getPlanDefinition(contractType, category)?.instrumentsUniverseIsins ??
    (instrumentsUniversePrefillIsins === undefined
      ? []
      : forEnvironment(instrumentsUniversePrefillIsins, environment))
  );
}

/** Name of a plan category or sub-plan, which an insurer's offer may replace with its own */
export function getPlanTypeName(
  planType: B2bSavingPlanType | B2bSavingPlanCategory,
  contractType: B2bContractType | null | undefined,
): string {
  const names = getPlanDefinition(contractType, b2bCompanyPlanTypeToCategory(planType))?.names;
  return names?.[planType] ?? B2bCompanyPlanTypeParameters[planType].name;
}

/**
 * Name of a plan's category without its sub-plans ("PEI Soles", "PEI" on ESSENTIEL contracts), as
 * named by the offer among `contractTypes` that covers it, the first one if several do
 */
export function getPlanCategoryShortName(
  planType: B2bSavingPlanType | B2bSavingPlanCategory,
  contractTypes: readonly B2bContractType[] | undefined,
): string {
  const category = b2bCompanyPlanTypeToCategory(planType);
  const contractType = contractTypes?.find((type) =>
    getContractTypeDefinition(type).planCategories.includes(category),
  );
  const [name] = getPlanTypeName(category, contractType).split(' - ');
  return name;
}

export function getInvestingProductShortName(
  planType: B2bSavingPlanCategory | B2bSavingPlanType,
): string {
  return B2bCompanyPlanTypeParameters[b2bCompanyPlanTypeToCategory(planType)].name_short;
}

const ZodB2bBracketLimit = z.union([z.number().int().min(0), z.literal('MAX')]);
export type B2bBracketLimit = z.TypeOf<typeof ZodB2bBracketLimit>;

export const getMaxPassLimit = (
  planType: B2bSavingPlanCategory | B2bSavingPlanType,
  passCeilingCents: number,
) => new Decimal(passCeilingCents).mul(getPctPassLimit(planType)).div(100).toNumber();

export const getPctPassLimit = (planType: B2bSavingPlanCategory | B2bSavingPlanType) =>
  EMPLOYEE_SAVINGS_PASS_PERCENTAGE[b2bCompanyPlanTypeToCategory(planType)];

export const ZodB2bCompanyPlanFeeDetails = z
  .array(
    z.object({
      amount: z.number(),
      paid_by: z.enum(B2bPlanFeePaidByValues),
      employee_status: ZodEmployeeStatus,
    }),
  )
  .length(3);
export type B2bCompanyPlanFeeDetails = z.infer<typeof ZodB2bCompanyPlanFeeDetails>;

export const ZodB2bCompanyPlanInstrumentEntryFee = z.object({
  instrument_id: ZodId,
  cash_source: ZodB2bCashSource.optional(),
  amount_pct: z.number().multipleOf(0.01).min(0).max(100),
  amount_pct_company: z.number().multipleOf(0.01).min(0).max(100).default(0),
  amount_pct_employee: z.number().multipleOf(0.01).min(0).max(100).default(0),
});

export type B2bCompanyPlanEntryFeeDetails = z.infer<typeof ZodB2bCompanyPlanInstrumentEntryFee>;

export const ZodB2bCompanyPlanInstrumentEntryFeeInput = z.object({
  ...ZodB2bCompanyPlanInstrumentEntryFee.shape,
  amount_pct: z.number().multipleOf(0.01).min(0).max(100).optional(),
  amount_pct_company: z.number().multipleOf(0.01).min(0).max(100).optional(),
  amount_pct_employee: z.number().multipleOf(0.01).min(0).max(100).optional(),
});
export type B2bCompanyPlanEntryFeeDetailsInput = z.infer<
  typeof ZodB2bCompanyPlanInstrumentEntryFeeInput
>;

const isUniqueEntryFeePerInstrumentAndCashSource = (
  fees: { instrument_id: number; cash_source?: string }[],
) => {
  const keys = fees.map((fee) => `${fee.instrument_id}:${fee.cash_source ?? ''}`);
  return new Set(keys).size === keys.length;
};

export const ZodB2bCompanyPlanFeesBase = z.object({
  management: ZodB2bCompanyPlanFeeDetails,
  entry_fee: ZodB2bCompanyPlanInstrumentEntryFee.array().refine(
    isUniqueEntryFeePerInstrumentAndCashSource,
    'entry_fee_must_be_unique_per_instrument_and_cash_source',
  ),
});

const ZodB2bCompanyPlanFees = ZodB2bCompanyPlanFeesBase.refine(
  (data) =>
    EmployeeStatusValues.every((employmentStatus) =>
      data.management.some((item) => item.employee_status === employmentStatus),
    ),
  { message: 'All employee statuses must be covered by management fees' },
);

const ZodCreateB2bCompanyPlanFees = z
  .object({
    ...ZodB2bCompanyPlanFeesBase.omit({
      entry_fee: true,
    }).shape,
    entry_fee: ZodB2bCompanyPlanInstrumentEntryFeeInput.array()
      .default([])
      .refine(
        isUniqueEntryFeePerInstrumentAndCashSource,
        'entry_fee_must_be_unique_per_instrument_and_cash_source',
      ),
  })
  .refine(
    (data) =>
      EmployeeStatusValues.every((employmentStatus) =>
        data.management.some((item) => item.employee_status === employmentStatus),
      ),
    { message: 'All employee statuses must be covered by management fees' },
  );

const B2bManagedPlanRebalancingFrequencyValues = [
  '1_month',
  '2_months',
  '3_months',
  '4_months',
  '6_months',
  '12_months',
] as const;
export type B2bManagedPlanRebalancingFrequency =
  (typeof B2bManagedPlanRebalancingFrequencyValues)[number];
export const ZodB2bManagedPlanRebalancingFrequency = z.enum(
  B2bManagedPlanRebalancingFrequencyValues,
);

const B2bManagedPlanManagementFeeChargeFrequencyValues = [
  '1_month',
  '2_months',
  '3_months',
  '4_months',
  '6_months',
  '12_months',
] as const;
export type B2bManagedPlanManagementFeeChargeFrequency =
  (typeof B2bManagedPlanManagementFeeChargeFrequencyValues)[number];
export const ZodB2bManagedPlanManagementFeeChargeFrequency = z.enum(
  B2bManagedPlanManagementFeeChargeFrequencyValues,
);

export type B2bCompanyPlanFees = z.infer<typeof ZodB2bCompanyPlanFees>;

export const ZodCombinedIdsString = z.string().refine(
  (val) => {
    const parts = val.split('_');
    return parts.every((part) => !isNaN(Number(part)) && Number.isInteger(Number(part)));
  },
  {
    message: "String must be equal to integer numbers joined with '_'",
  },
);

const ZodCombinedIDPathParam = createZodParamsValidator(
  z.object({
    id: ZodCombinedIdsString,
  }),
);

const ZodB2bPlanRetirementAge = z.number().int().min(55).max(70).nullable().optional();

const ZodB2bCompanySharesPlan = z.object({
  id: ZodId,
  name: ZodTrimmedString,
  plan_label: ZodTrimmedString.optional().nullable(),
  type: ZodB2bSavingPlanType,
  rebalancing_frequency: ZodB2bManagedPlanRebalancingFrequency.nullable(),
  management_fee_charge_frequency:
    ZodB2bManagedPlanManagementFeeChargeFrequency.optional().default('3_months'),
  retirement_age: ZodB2bPlanRetirementAge,
  instruments_universe: z.array(z.number().int()),
  instruments_locked: z.array(z.number().int()).optional(),
});

export type B2bCompanySharesPlan = z.TypeOf<typeof ZodB2bCompanySharesPlan>;

const ZodInstrumentsCashSources = z.array(
  z.object({
    instrument_id: ZodId,
    cash_sources: z.array(ZodB2bCashSource).nullable(),
    is_matching_contributions_enabled: z.boolean().nullable(),
  }),
);
export type B2bPlanInstrumentsCashSources = z.TypeOf<typeof ZodInstrumentsCashSources>;

export const B2bCompanyPlanMCPoolType = ['cumulative_contributions', 'per_contribution'] as const;
export const ZodB2bCompanyPlanMCPoolType = z.enum(B2bCompanyPlanMCPoolType);

// Annual ceiling in cents, null when the plan offers no unilateral matching contribution
const ZodUnilateralMatchingContributionLimit = z
  .number()
  .int()
  .min(0)
  .max(EMPLOYEE_SAVINGS_MAX_UNILATERAL_MATCHING_CONTRIBUTION_CENTS)
  .nullable();

export const ZodB2bCompanyPlan = z.object({
  id: ZodCombinedIdsString,
  shares_plans: z.array(ZodB2bCompanySharesPlan),
  b2b_contract_id: ZodId,
  contract_type: ZodB2bContractType,
  company_id: ZodId,
  name: ZodTrimmedString,
  plan_label: ZodTrimmedString.optional().nullable(),
  social_contribution: z.number(),
  category: ZodB2bSavingPlanCategory,
  matching_contribution_config: ZodMatchingContributionConfig.nullable(),
  has_custom_matching_contribution_config: z.boolean().nullable(),
  matching_contribution_computed_by: z.enum(B2bMatchingContributionComputedByValues).nullable(),
  minimum_tenure: z.number().int().min(0).max(EMPLOYEE_SAVINGS_MAX_MINIMUM_TENURE_MONTHS),
  minimum_tenure_reference_date: ZodTrimmedString.regex(/^\d{2}\/\d{2}$/)
    .nullable()
    .optional(),
  fees: ZodB2bCompanyPlanFees,
  cash_sources: z.array(ZodB2bCashSource),
  default_fund_instrument_id: ZodId.nullable(),
  is_cash_sources_per_instrument: z.boolean(),
  instruments_cash_sources_libre: ZodInstrumentsCashSources,
  matching_contribution_pool_type: ZodB2bCompanyPlanMCPoolType,
  matching_contribution_product_limit: z.number().int().nonnegative().nullable().default(null),
  unilateral_matching_contribution_limit: ZodUnilateralMatchingContributionLimit.default(null),
  created_at: z.date(),
  updated_at: z.date(),
  default_investor_profile: z
    .object({
      id: ZodId,
      name: ZodTrimmedString,
      risk_profile: ZodEmployeeSavingsRiskProfile,
    })
    .nullable(),
  other_investor_profiles: z
    .array(
      z.object({
        id: ZodId,
        name: ZodTrimmedString,
        risk_profile: ZodEmployeeSavingsRiskProfile,
      }),
    )
    .readonly(),
  ccb_config: ZodB2bCcbConfig.nullable(),
});

export type B2bCompanyPlan = z.TypeOf<typeof ZodB2bCompanyPlan>;

const ZodB2bCompanyPlansFilters = z
  .object({
    id: createZodArrayable(ZodId),
    b2b_contract_id: createZodArrayable(ZodId),
    company_id: createZodArrayable(ZodId),
  })
  .partial();

export type B2bCompanyPlansFilters = z.TypeOf<typeof ZodB2bCompanyPlansFilters>;

export const ZodB2bCompanyPlansQuery = buildZodBoListQueryParsed(
  ZodB2bCompanyPlansFilters,
  ['created_at'],
  {
    range: [0, 100],
  },
);
export type B2bCompanyPlansQuery = z.TypeOf<typeof ZodB2bCompanyPlansQuery>;

export const ZodGetB2bCompaniesPlansResponse = z.array(ZodB2bCompanyPlan);
export type GetB2bCompaniesPlansResponse = z.TypeOf<typeof ZodGetB2bCompaniesPlansResponse>;

const ZodFlatB2bCompanyPlanItem = z.object({
  id: ZodId,
  name: ZodTrimmedString,
  type: ZodB2bSavingPlanType,
  b2b_contract_id: ZodId,
  company_id: ZodId.nullable(),
});
export type FlatB2bCompanyPlanItem = z.TypeOf<typeof ZodFlatB2bCompanyPlanItem>;

const ZodFlatB2bCompanyPlansFilters = z
  .object({
    id: createZodArrayable(ZodId),
  })
  .partial();

export const ZodFlatB2bCompanyPlansQuery = buildZodBoListQueryParsed(
  ZodFlatB2bCompanyPlansFilters,
  ['id', 'name'],
  { range: [0, 14] },
);
export type FlatB2bCompanyPlansQuery = z.TypeOf<typeof ZodFlatB2bCompanyPlansQuery>;

export const ZodGetFlatB2bCompaniesPlansResponse = z.array(ZodFlatB2bCompanyPlanItem);
export type GetFlatB2bCompaniesPlansResponse = z.TypeOf<typeof ZodGetFlatB2bCompaniesPlansResponse>;

export const getFlatB2bCompaniesPlansEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodFlatB2bCompanyPlansQuery,
  responseBody: ZodGetFlatB2bCompaniesPlansResponse,
});

export type GetFlatB2bCompaniesPlansEndpoint = GetEndpointType<
  typeof getFlatB2bCompaniesPlansEndpoint
>;

export const ZodPostB2bCompanyPlanBody = z
  .object({
    ...ZodB2bCompanyPlan.omit({
      id: true,
      contract_type: true,
      name: true,
      social_contribution: true,
      created_at: true,
      updated_at: true,
      category: true,
      default_investor_profile: true,
      other_investor_profiles: true,
      is_cash_sources_per_instrument: true,
      instruments_cash_sources_libre: true,
      matching_contribution_pool_type: true,
      has_custom_matching_contribution_config: true,
      ccb_config: true,
    }).shape,
    type: ZodB2bSavingPlanCategory,
    shares_plans: z.array(
      z.object({
        ...ZodB2bCompanySharesPlan.omit({
          id: true,
          name: true,
          instruments_universe: true,
        }).shape,
        id_external: z.array(ZodTrimmedString).optional().nullable(),
      }),
    ),
    matching_contribution_pool_type: ZodB2bCompanyPlanMCPoolType.optional().default(
      'cumulative_contributions',
    ),
    fees: ZodCreateB2bCompanyPlanFees,
    instruments_universe: z.array(ZodId),
    // For Axa migration, we do get investment universe information for piloted plans
    instruments_universe_piloted: z.array(ZodId).optional(),
    instruments_cash_sources_libre: ZodInstrumentsCashSources.optional(),
    default_fund_instrument_id: ZodId.optional(),
    default_custom_risk_profile_id: ZodId.optional(),
    other_custom_risk_profile_ids: ZodId.array().max(20).default([]).optional(),
    is_cash_sources_per_instrument: z.boolean().optional().default(false),
    ccb_config: ZodB2bCcbConfig.optional().nullable(),
  })
  .refine(
    (data) =>
      data.fees.entry_fee
        .map((row) => row.instrument_id)
        .every(
          (id) =>
            data.instruments_universe.includes(id) ||
            (data.instruments_universe_piloted ?? []).includes(id),
        ),
    'entry_fee_instrument_id_must_be_in_instruments_universe',
  )
  .refine((data) => {
    return data.fees.entry_fee.every((fee) => {
      if (!fee.cash_source) {
        return true;
      }
      if (!data.cash_sources.includes(fee.cash_source)) {
        return false;
      }
      const instrumentSources = data.instruments_cash_sources_libre?.find(
        (entry) => entry.instrument_id === fee.instrument_id,
      );
      if (!instrumentSources?.cash_sources) {
        return true;
      }
      return instrumentSources.cash_sources.includes(fee.cash_source);
    });
  }, 'entry_fee_cash_source_must_be_enabled_on_plan')
  .refine(
    (data) => !!data.matching_contribution_config === !!data.matching_contribution_computed_by,
    'matching_contribution_config_and_computed_by_must_be_both_present_or_both_absent',
  )
  .refine(
    (data) =>
      !data.matching_contribution_config ||
      !data.matching_contribution_computed_by ||
      data.matching_contribution_computed_by !== 'company' ||
      data.matching_contribution_config.type === 'company_simple',
    'matching_contribution_computed_by_company_should_have_config_company_simple',
  )
  .refine(
    (data) =>
      !data.matching_contribution_config ||
      !data.matching_contribution_computed_by ||
      data.matching_contribution_computed_by !== 'distributor' ||
      data.matching_contribution_config.type !== 'company_simple',
    'matching_contribution_computed_by_distributor_should_have_config_different_than_company_simple',
  )
  .refine((data) => {
    if (!data.is_cash_sources_per_instrument && data.instruments_cash_sources_libre) {
      return false;
    }
    if (
      data.instruments_cash_sources_libre?.some((config) =>
        config.cash_sources?.some((source) => !data.cash_sources.includes(source)),
      )
    ) {
      return false;
    }
    if (
      !data.matching_contribution_config &&
      data.instruments_cash_sources_libre?.some(
        (config) => config.is_matching_contributions_enabled,
      )
    ) {
      return false;
    }
    return true;
  }, 'cash_source_per_instrument_invalid_config')
  .refine(
    (data) =>
      match(data.type)
        .with(
          'PEE',
          'PEEG',
          'PERECO',
          'PERECOG',
          'PERCO',
          () =>
            data.shares_plans.findIndex((plan) =>
              ['PEE_LIBRE', 'PEEG_LIBRE', 'PERECO_LIBRE', 'PERECOG_LIBRE', 'PERCO_LIBRE'].includes(
                plan.type,
              ),
            ) === -1 || G.isNotNullable(data.default_fund_instrument_id),
        )
        .with('ARTICLE_82', 'ARTICLE_83', 'PERO', 'PERU', 'PER', 'PERECOER', () =>
          G.isNullable(data.default_fund_instrument_id),
        )
        // PEI and PERCOI and PERECOI can either be SOLES, in which cases default_fund_instrument_id is hardcoded in BE,
        // either not, so we don't enforce validation rules anymore on those ones
        .with('PEI', 'PERCOI', 'PERECOI', () => true)
        .with('ER_EXTERNAL', () => data.shares_plans.every((plan) => plan.type === data.type))
        .exhaustive(),
    'default_fund_instrument_id is mandatory for PEE, PEEG, PERECO & PERECOG libre, for other type of plan it is not accepted',
  )
  .refine(
    (data) =>
      match(data.type)
        .with(
          'PEE',
          'PEEG',
          'PERECO',
          'PERECOG',
          'PERCO',
          () =>
            data.shares_plans.findIndex((plan) =>
              [
                'PEE_PROFILE',
                'PEEG_PROFILE',
                'PERECO_PILOTE',
                'PERECOG_PILOTE',
                'PERCO_PILOTE',
              ].includes(plan.type),
            ) === -1 ||
            (G.isNotNullable(data.default_custom_risk_profile_id) &&
              G.isNotNullable(data.other_custom_risk_profile_ids)),
        )
        .with(
          'ARTICLE_82',
          'ARTICLE_83',
          'PERO',
          'PERU',
          'PER',
          'PERECOER',
          () =>
            G.isNullable(data.default_custom_risk_profile_id) &&
            (G.isNullable(data.other_custom_risk_profile_ids) ||
              data.other_custom_risk_profile_ids.length === 0),
        )
        // PEI and PERCOI and PERECOI can either be SOLES, in which cases default_custom_risk_profile_id is hardcoded in BE,
        // either not, so we don't enforce validation rules anymore on those ones
        .with('PEI', 'PERCOI', 'PERECOI', () => true)
        .with('ER_EXTERNAL', () => data.shares_plans.every((plan) => plan.type === data.type))
        .exhaustive(),
    'default_custom_risk_profile_id is mandatory for PEE, PEEG, PERECO and PERECOG managed plans and is not accepted for other plan category',
  )
  .refine(
    (data) =>
      data.shares_plans.every(
        (plan) => G.isNullable(plan.retirement_age) || isPerecoiPilotePlan(plan.type),
      ),
    'retirement_age_only_allowed_on_pereco_pilote_plans',
  );

const ZodPostB2bCompanyPlanResponse = z.object({
  id: ZodId,
  plans: z.array(
    z.object({
      id: ZodId,
      type: ZodB2bSavingPlanType,
      plan_label: ZodTrimmedString.optional().nullable(),
    }),
  ),
});

export const getB2bCompaniesPlansEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodB2bCompanyPlansQuery,
  responseBody: ZodGetB2bCompaniesPlansResponse,
});
export type GetB2bCompaniesPlansEndpoint = GetEndpointType<typeof getB2bCompaniesPlansEndpoint>;

export const postB2bCompaniesPlansEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bCompanyPlanBody,
  responseBody: ZodPostB2bCompanyPlanResponse,
});
export type PostB2bCompaniesPlansEndpoint = GetEndpointType<typeof postB2bCompaniesPlansEndpoint>;
export type PostB2bCompanyPlanBody = z.infer<typeof ZodPostB2bCompanyPlanBody>;

const ZodPutB2bCompanySharesPlanUpdate = z.object({
  plan_id: ZodId,
  rebalancing_frequency: ZodB2bManagedPlanRebalancingFrequency.nullable().optional(),
  plan_label: ZodTrimmedString.nullable().optional(),
  retirement_age: ZodB2bPlanRetirementAge,
});

const ZodPutB2bCompanyPlanBody = z
  .object({
    data: z.object({
      matching_contributions_enabled: z.boolean().optional(),
      matching_contribution_config: ZodMatchingContributionConfig.nullable(),
      matching_contribution_pool_type: ZodB2bCompanyPlanMCPoolType.optional(),
      instruments_universe: z
        .array(
          z.object({
            plan_id: ZodId,
            instruments_universe: z.array(ZodId),
          }),
        )
        .optional(),
      entry_fee: ZodB2bCompanyPlanInstrumentEntryFeeInput.array()
        .refine(
          isUniqueEntryFeePerInstrumentAndCashSource,
          'entry_fee_must_be_unique_per_instrument_and_cash_source',
        )
        .optional(),
      cash_sources: z.array(ZodB2bCashSource).optional(),
      is_cash_sources_per_instrument: z.boolean().optional(),
      instruments_cash_sources_libre: ZodInstrumentsCashSources.optional(),
      default_fund_instrument_id: ZodId.optional(),
      default_custom_risk_profile_id: ZodId.optional(),
      other_custom_risk_profile_ids: ZodId.array().max(20).optional(),
      shares_plans: z.array(ZodPutB2bCompanySharesPlanUpdate).optional(),
      ccb_config: ZodB2bCcbConfig.nullable().optional(),
      unilateral_matching_contribution_limit: ZodUnilateralMatchingContributionLimit.optional(),
    }),
    options: z.object({
      applies_at: z.enum(['now', 'next-year']),
      matching_contribution_computed_by: z.enum(B2bMatchingContributionComputedByValues).nullable(),
      matching_contribution_product_limit: z.number().nonnegative().int().nullable().optional(),
    }),
  })
  .refine(
    (data) =>
      !data.data.matching_contributions_enabled || data.data.matching_contribution_config !== null,
    'matching_contribution_config_required_when_enabled',
  )
  .refine(
    (data) =>
      data.data.matching_contributions_enabled || data.data.matching_contribution_config === null,
    'matching_contribution_config_must_be_null_when_disabled',
  )
  .refine(
    (data) =>
      data.data.is_cash_sources_per_instrument !== false ||
      !data.data.instruments_cash_sources_libre,
    'cash_source_per_instrument_not_allowed_with_uniform_settings',
  )
  // Mirror the POST body refinements for per-instrument cash sources
  .refine((data) => {
    const { cash_sources: cashSources, instruments_cash_sources_libre: instrumentsCashSources } =
      data.data;
    if (!instrumentsCashSources) {
      return true;
    }
    // An instrument may only allow cash sources enabled at plan level.
    if (
      instrumentsCashSources.some((config) =>
        config.cash_sources?.some((source) => !(cashSources ?? []).includes(source)),
      )
    ) {
      return false;
    }
    // Matching contribution cannot be enabled per instrument when the plan has no MC config.
    if (
      !data.data.matching_contribution_config &&
      instrumentsCashSources.some((config) => config.is_matching_contributions_enabled)
    ) {
      return false;
    }
    return true;
  }, 'cash_source_per_instrument_invalid_config')
  .refine((data) => {
    const { entry_fee: entryFees, cash_sources: cashSources } = data.data;
    if (!entryFees || !cashSources) {
      return true;
    }
    return entryFees.every((fee) => !fee.cash_source || cashSources.includes(fee.cash_source));
  }, 'entry_fee_cash_source_must_be_enabled_on_plan');

export const putB2bCompaniesPlansEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodCombinedIDPathParam,
  body: ZodPutB2bCompanyPlanBody,
  responseBody: z.object({ ...ZodSuccessResponse.shape, id: ZodCombinedIdsString }),
});
export type PutB2bCompaniesPlansEndpoint = GetEndpointType<typeof putB2bCompaniesPlansEndpoint>;

export const getB2bCompanyPlanEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodCombinedIDPathParam,
  responseBody: ZodB2bCompanyPlan,
});
export type GetB2bCompanyPlanEndpoint = GetEndpointType<typeof getB2bCompanyPlanEndpoint>;
